import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  listConsents,
  revokeConsent,
  type ListConsentsResponse,
  type ConsentSummary,
  type LimitWithUsage,
} from '../services/consent';

/** Map consent status to a colored badge */
function StatusBadge({ status }: { status: string }) {
  const styles: Record<string, string> = {
    authorized: 'bg-green-50 text-green-700',
    expired: 'bg-gray-100 text-gray-500',
    revoked: 'bg-red-50 text-red-700',
    rejected: 'bg-red-50 text-red-600',
    pending_authorization: 'bg-yellow-50 text-yellow-700',
    awaiting_psu_authorization: 'bg-yellow-50 text-yellow-700',
  };

  const labels: Record<string, string> = {
    authorized: 'Active',
    expired: 'Expired',
    revoked: 'Revoked',
    rejected: 'Rejected',
    pending_authorization: 'Pending',
    awaiting_psu_authorization: 'Awaiting Authorization',
  };

  const style = styles[status] || 'bg-gray-100 text-gray-600';
  const label = labels[status] || status;

  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${style}`}
    >
      {label}
    </span>
  );
}

/** Progress bar for limit usage */
function LimitBar({ label, limit }: { label: string; limit: LimitWithUsage }) {
  const pct = limit.threshold > 0 ? Math.min((limit.used / limit.threshold) * 100, 100) : 0;
  const color = pct >= 90 ? 'bg-red-500' : pct >= 70 ? 'bg-yellow-500' : 'bg-primary-500';

  return (
    <div>
      <div className="flex justify-between text-xs text-gray-500 mb-1">
        <span>{label}</span>
        <span>
          {limit.used} / {limit.threshold}
        </span>
      </div>
      <div className="h-1.5 rounded-full bg-gray-100">
        <div
          className={`h-1.5 rounded-full ${color} transition-all`}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

/** Format an ISO date to a readable string */
function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

/** Days until a date; negative means days ago */
function daysUntil(iso: string): number {
  const diff = new Date(iso).getTime() - Date.now();
  return Math.ceil(diff / (1000 * 60 * 60 * 24));
}

/** Single consent card */
function ConsentCard({
  consent,
  onRevoke,
  revoking,
}: {
  consent: ConsentSummary;
  onRevoke: (consentId: string) => void;
  revoking: boolean;
}) {
  const remaining = daysUntil(consent.valid_until);
  const isActive = consent.status === 'authorized';

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 space-y-4">
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="text-sm font-semibold text-gray-900 truncate">
            {consent.label || `Consent ${consent.consent_id.slice(0, 8)}...`}
          </h3>
          <p className="text-xs text-gray-400 mt-0.5 font-mono truncate" title={consent.consent_id}>
            {consent.consent_id}
          </p>
          {consent.aspsp_consent_id !== consent.consent_id && (
            <p className="text-xs text-gray-400 font-mono truncate" title={`Bank ID: ${consent.aspsp_consent_id}`}>
              Bank: {consent.aspsp_consent_id}
            </p>
          )}
        </div>
        <StatusBadge status={consent.status} />
      </div>

      {/* Scope badges */}
      {consent.scope.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {consent.scope.map((s) => (
            <span
              key={s}
              className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-50 text-blue-700"
            >
              {s}
            </span>
          ))}
        </div>
      )}

      {/* Validity */}
      <div className="text-xs text-gray-500">
        <span>{formatDate(consent.valid_from)}</span>
        <span className="mx-1.5">&rarr;</span>
        <span>{formatDate(consent.valid_until)}</span>
        {isActive && remaining > 0 && (
          <span className="ml-2 text-gray-400">
            ({remaining} day{remaining !== 1 ? 's' : ''} left)
          </span>
        )}
        {isActive && remaining <= 0 && (
          <span className="ml-2 text-red-500 font-medium">expired</span>
        )}
      </div>

      {/* Limits with usage bars */}
      {isActive && (
        <div className="space-y-2">
          <LimitBar label="Payments today" limit={consent.limits.max_payments_per_day} />
          <LimitBar label="Payments this month" limit={consent.limits.max_payments_per_month} />
          <div className="flex justify-between text-xs text-gray-500">
            <span>Max per payment</span>
            <span>
              {consent.limits.max_amount_per_payment.toLocaleString()} {consent.limits.currency}
            </span>
          </div>
        </div>
      )}

      {/* Usage summary */}
      <div className="flex items-center justify-between text-xs text-gray-400 pt-2 border-t border-gray-100">
        <span>
          {consent.usage_summary.total_payments} total payment
          {consent.usage_summary.total_payments !== 1 ? 's' : ''}
        </span>
        {consent.usage_summary.last_payment_at && (
          <span>Last: {formatDate(consent.usage_summary.last_payment_at)}</span>
        )}
      </div>

      {/* Revoke button — only for active/pending consents */}
      {(isActive || consent.status === 'pending_authorization' || consent.status === 'awaiting_psu_authorization') && (
        <button
          disabled={revoking}
          onClick={() => {
            if (window.confirm('Are you sure you want to revoke this consent? This action cannot be undone.')) {
              onRevoke(consent.consent_id);
            }
          }}
          className="w-full text-center text-xs font-medium text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg py-1.5 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {revoking ? 'Revoking...' : 'Revoke Consent'}
        </button>
      )}
    </div>
  );
}

export default function ConsentsPage() {
  const navigate = useNavigate();
  const [data, setData] = useState<ListConsentsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [revoking, setRevoking] = useState<string | null>(null);
  const [showAll, setShowAll] = useState(false);

  const handleRevoke = async (consentId: string) => {
    setRevoking(consentId);
    try {
      await revokeConsent(consentId);
      // Refresh the list after successful revocation
      await fetchConsents(showAll);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to revoke consent');
    } finally {
      setRevoking(null);
    }
  };

  const fetchConsents = async (all: boolean) => {
    setLoading(true);
    setError(null);
    try {
      const response = await listConsents(!all);
      setData(response);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load consents');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchConsents(showAll);
  }, [showAll]);

  if (loading) {
    return (
      <div className="text-center py-12">
        <svg
          className="animate-spin text-primary-600 mx-auto mb-3"
          style={{ width: '32px', height: '32px' }}
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            style={{ opacity: 0.25 }}
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          />
          <path
            style={{ opacity: 0.75 }}
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          />
        </svg>
        <p style={{ fontSize: '14px', color: '#6b7280' }}>Loading consents...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-2xl mx-auto space-y-6">
        <h1 className="text-2xl font-bold text-gray-900">My Consents</h1>
        <div className="rounded-lg bg-red-50 border border-red-200 p-4">
          <p className="text-sm text-red-700">{error}</p>
          <button
            onClick={() => fetchConsents(showAll)}
            className="mt-2 text-sm font-medium text-red-700 hover:text-red-800 underline"
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  const banks = data?.banks || [];
  const totalConsents = banks.reduce((sum, b) => sum + b.consents.length, 0);

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">My Consents</h1>
          <p className="mt-1 text-sm text-gray-500">
            {totalConsents} consent{totalConsents !== 1 ? 's' : ''} across {banks.length} bank
            {banks.length !== 1 ? 's' : ''}
          </p>
        </div>
        <div className="flex items-center gap-4">
          <label className="inline-flex items-center gap-2 cursor-pointer select-none">
            <span className="text-sm text-gray-600">All</span>
            <span className="relative inline-block w-11 h-6">
              <input
                type="checkbox"
                checked={showAll}
                onChange={(e) => setShowAll(e.target.checked)}
                className="sr-only"
              />
              <span
                className={`block w-11 h-6 rounded-full transition-colors ${
                  showAll ? 'bg-primary-600' : 'bg-gray-300'
                }`}
              />
              <span
                className={`absolute top-0.5 left-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform ${
                  showAll ? 'translate-x-5' : 'translate-x-0'
                }`}
              />
            </span>
          </label>
          <button
            onClick={() => navigate('/banks')}
            className="rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 transition-colors"
          >
            Connect Bank
          </button>
        </div>
      </div>

      {banks.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 text-center">
          <p className="text-gray-500 mb-4">
            {showAll
              ? 'No consents yet. Connect a bank to get started.'
              : 'No active consents. Toggle "All" to see expired and revoked consents, or connect a new bank.'}
          </p>
          <button
            onClick={() => navigate('/banks')}
            className="rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 transition-colors"
          >
            Browse Banks
          </button>
        </div>
      ) : (
        banks.map((bank) => (
          <div key={bank.aspsp} className="space-y-3">
            {/* Bank header */}
            <div className="flex items-center gap-2">
              <h2 className="text-base font-semibold text-gray-900">{bank.bank_name}</h2>
              <span className="text-xs text-gray-400 font-mono">{bank.aspsp}</span>
            </div>

            {/* Consent cards grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {bank.consents.map((consent) => (
                <ConsentCard
                  key={consent.consent_id}
                  consent={consent}
                  onRevoke={handleRevoke}
                  revoking={revoking === consent.consent_id}
                />
              ))}
            </div>
          </div>
        ))
      )}
    </div>
  );
}
